// Sign-in screen — magic-link flow in three calm steps.
//
// Visual primary: Apple Marketing pages (one confident point per screen,
//   large display type, generous vertical rhythm, simple designed hero
//   visuals — here a hand-drawn paper plane).
// Visual secondary: Linear (the input + button chrome).
//
// Wired to the useAuth hook (packages/hooks): requestLink → verifyCode.

import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useAuth } from '@vibeshell/hooks';
import { ArrowRight, Check, ChevronLeft } from 'lucide-preact';
import { t } from '../lib/i18n';

type Step = 'email' | 'code' | 'done';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CONSENT_VERSION = '2026-06-01';

/** §7 clickwrap — record agreement to the current Terms + Privacy server-side. */
async function recordSignupConsent(): Promise<void> {
  for (const type of ['terms', 'privacy'] as const) {
    try {
      await fetch('/v1/consent', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ type, version: CONSENT_VERSION })
      });
    } catch {
      /* best-effort — the session cookie is what proves identity */
    }
  }
}

export function SignIn(props: { onClose: () => void }): JSX.Element {
  const { session, requestLink, verifyCode } = useAuth();
  const [step, setStep] = useState<Step>(session.signedIn ? 'done' : 'email');
  const [email, setEmail] = useState(session.email ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // §L6 explicit clickwrap — consent is a user GESTURE, not an implication.
  const [agreed, setAgreed] = useState(false);
  const [devHint, setDevHint] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  async function submitEmail(e: Event): Promise<void> {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError(t('si.errEmail'));
      return;
    }
    setError('');
    setDevHint(null);
    setBusy(true);
    const result = await requestLink(email.trim().toLowerCase());
    setBusy(false);
    if (result.ok) {
      setStep('code');
      // Dev-only courtesy: when the gateway can't reach Resend, it
      // returns the OTP back to us so the dev can sign in without
      // combing the terminal. Surface the actual returned code only —
      // never hint at any backend-only bypass codes, which would
      // telegraph the existence of an internal backdoor to every
      // visitor who happens to trigger a delivery failure.
      if (result.devCode) {
        setDevHint(`Dev mode, email isn’t configured here. Use code ${result.devCode}.`);
      } else if (result.devMode) {
        // No code returned + dev mode → emails are silently dropped in
        // this environment. Tell the dev to check the worker logs.
        setDevHint('Dev mode, email isn’t wired here. Check the worker logs for the OTP.');
      }
    } else {
      // True failure — gateway unreachable or returned non-2xx. Generic
      // safe copy; no mention of backend bypass paths.
      setError(t('si.errSend'));
    }
  }

  async function submitCode(e: Event): Promise<void> {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError(t('si.errCode'));
      return;
    }
    setError('');
    setBusy(true);
    const next = await verifyCode(email.trim().toLowerCase(), code);
    setBusy(false);
    if (next) {
      setStep('done');
      // §7 clickwrap — continuing past sign-in records agreement to the current
      // Terms + Privacy (version + timestamp), server-side. Best-effort.
      void recordSignupConsent();
    } else setError(t('si.errCodeBad'));
  }

  return (
    <div class="signin-screen">
      <button type="button" class="screen-back signin-back" onClick={props.onClose} aria-label={t('common.goBack')}>
        <ChevronLeft size={18} strokeWidth={2.25} />
        {t('common.back')}
      </button>

      <div class="signin-stage">
        {step === 'email' && (
          <form onSubmit={submitEmail} key="email" class="signin-step">
            <Hero
              eyebrow={t('si.welcome')}
              title={t('si.title')}
              sub={t('si.sub')}
            />
            <input
              type="email"
              inputMode="email"
              autocomplete="email"
              aria-label={t('si.emailAria')}
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="you@example.com"
              autoFocus
              class="signin-input"
            />
            {error && <ErrorLine>{error}</ErrorLine>}
            {/* §L6 explicit clickwrap — the checkbox gates Continue; consent is
                recorded server-side after verify (version + timestamp). */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '14px 2px 0', cursor: 'pointer', textAlign: 'left' }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed((e.target as HTMLInputElement).checked)}
                style={{ marginTop: 2, width: 16, height: 16, accentColor: 'var(--color-primary)', flexShrink: 0 }}
                aria-label={t('si.agreeAria')}
              />
              <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--fg-subtle)' }}>
                {t('si.consentPre')}
                <a href="/legal/terms" target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', fontWeight: 500 }}>{t('si.terms')}</a>{t('si.consentMid')}
                <a href="/legal/privacy" target="_blank" rel="noopener" style={{ color: 'var(--color-primary)', fontWeight: 500 }}>{t('si.privacy')}</a>.
              </span>
            </label>
            <div style={{ marginTop: 12 }}>
              <PrimaryButton busy={busy} label={t('si.continue')} disabled={!agreed} />
            </div>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} key="code" class="signin-step">
            <PaperPlane />
            <Hero
              eyebrow={t('si.checkEmail')}
              title={t('si.enterCode')}
              sub={t('si.codeSent', { email })}
            />
            {devHint && <DevHint>{devHint}</DevHint>}
            <input
              ref={codeRef}
              inputMode="numeric"
              autocomplete="one-time-code"
              aria-label={t('si.codeAria')}
              value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              class="signin-input signin-input--code"
            />
            {error && <ErrorLine>{error}</ErrorLine>}
            <PrimaryButton busy={busy} label={t('si.verify')} />
            <button
              type="button"
              class="signin-textlink"
              onClick={() => { setStep('email'); setCode(''); setError(''); setDevHint(null); }}
            >
              {t('si.differentEmail')}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div key="done" class="signin-step signin-step--centered">
            <div class="signin-check">
              <Check size={32} color="#fff" strokeWidth={3} />
            </div>
            <Hero
              eyebrow={t('si.youreIn')}
              title={t('si.signedIn')}
              sub={t('si.doneSub', { email })}
            />
            <button type="button" class="cta-press signin-primary" onClick={props.onClose}>
              {t('si.startBuilding')} <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── pieces ─────────────────────────────────────────────────────────────────

function Hero(props: { eyebrow: string; title: string; sub: string }): JSX.Element {
  return (
    <div class="signin-hero">
      <p class="signin-hero__eyebrow">{props.eyebrow}</p>
      <h1 class="signin-hero__title">{props.title}</h1>
      <p class="signin-hero__sub">{props.sub}</p>
    </div>
  );
}

function PrimaryButton(props: { busy: boolean; label: string; disabled?: boolean }): JSX.Element {
  return (
    <button type="submit" class="cta-press signin-primary" disabled={props.busy || props.disabled}>
      {props.busy ? t('si.oneMoment') : props.label}
      {!props.busy && <ArrowRight size={16} />}
    </button>
  );
}

function ErrorLine(props: { children: JSX.Element | string }): JSX.Element {
  return <p class="signin-error" role="alert">{props.children}</p>;
}

/**
 * Dev/founder courtesy notice. Pink-tinted glass card with the OTP the
 * gateway echoed back when Resend was unavailable. Never rendered in
 * production — the gateway only returns `devCode` outside prod.
 */
function DevHint(props: { children: JSX.Element | string }): JSX.Element {
  // The DEV chip is mandatory — without it, an OTP echoed back by the
  // gateway during a Resend outage reads as ordinary product copy in
  // screenshots forwarded to customer support. (NIGHT_AUDIT §4.6.)
  return (
    <p class="signin-devhint" role="status">
      <span class="signin-devhint__chip" aria-label="Development mode">DEV</span>
      <span class="signin-devhint__body">{props.children}</span>
    </p>
  );
}

function PaperPlane(): JSX.Element {
  return (
    <svg class="signin-plane" width="56" height="56" viewBox="0 0 56 56" fill="none">
      <path d="M50 6 6 26l18 6 6 18 20-44Z" fill="var(--color-primary)" />
      <path d="M50 6 24 32l0 18" stroke="var(--bg-elevated)" stroke-width="2.5" stroke-linejoin="round" fill="none" opacity="0.55" />
    </svg>
  );
}
